import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css, useTheme as useStyledTheme } from 'styled-components'
import {
  Modal,
  Text,
  Flex,
  ModalProps,
  Button,
  Skeleton,
  AutoRenewIcon,
  InfoIcon,
  Message,
  useMatchBreakpoints,
} from 'vvs-uikit'
import NftSelectorView from 'components/NftSelectorView'
import NftBoostFactorCard, { Divider } from 'views/Farms/components/NftBoostFactorCard'
import NftBoostedAPRSection from 'views/Farms/components/NftBoostedAPRSection'
import { EmptyNFTs } from 'components/NftSelectorView/EmptyNFTs'
import { useDeserializedFarmFromPid } from 'state/farms/useDeserializedFarms'
import { getFarmVersionFromPid } from 'state/farms/farmsPidToVersion'
import { useTransactionAdder } from 'state/transactions/hooks'
import { NFT_TYPE } from 'config/constants/nftTypes'
import { BoostedAPRDetails, ConfirmStakedProps } from 'views/Farms/interfaces'
import { getArgoNftAddress, getVvsMinerMoleAddress } from 'utils/addressHelpers'
import { useUserMoles } from 'state/minerMole/useUserMoles'
import { MAX_NFT_SELECTABLE } from 'config/constants/farmBoost'
import { useStakeNftsAndBoostFarm } from 'state/farmBoost/useFarmBoostMutations'
import useGetBoostedAPRDetails, { useGetEcoFarmBoostedAPRDetails } from 'views/Farms/hooks/useGetBoostedAPRDetails'
import useToast from 'hooks/useToast'
import { ModalActions } from 'components/Modal'
import useNFTStakingApproval from 'views/Farms/hooks/useNFTStakingApproval'
import useNftBalanceOfUser from 'views/Farms/hooks/useNftBalanceOfUser'
import { ecoFarm } from 'config/constants/ecoBoost'
import { useFormatLayerForNftDetails } from 'views/Farms/hooks/useGetEcoFarmNftCollections'
import { NftDetails } from 'views/VvsMinerMoleMyMoles/types'
import { getRewarderV2AddressByPid, useStakeNfts } from 'hooks/rewarderV2/useRewarderV2Contract'
import { CALCULATE_TYPE } from '../hooks/useNftModalAprDetails'

const StyledMessage = styled(Message)`
  flex: 1;
  height: 60px;
  svg {
    margin-right: 10px;
  }
`

const StyledMessageText = styled(Text)`
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: #000000;
`

interface NftStakeModalInnerProps extends ModalProps {
  modalStep: NFT_STAKE
}

const StyledModal = styled(Modal)<NftStakeModalInnerProps>`
  border-radius: 18px;
  border: none;
  > div {
    max-height: 80vh;
    max-width: 570px;
  }
  ${(props) =>
    (props.modalStep === NFT_STAKE.approve ||
      props.modalStep === NFT_STAKE.emptyNfts ||
      props.modalStep === NFT_STAKE.loading) &&
    css`
      max-width: 420px;
    `}

  ${(props) =>
    props.modalStep === NFT_STAKE.emptyStaked &&
    css`
      min-width: 345px;

      ${({ theme }) => theme.mediaQueries.md} {
        min-width: 570px;
      }
    `}
`

const NFTLogo = styled.img`
  width: 100px;
  height: 88px;
`

const ApproveContract: React.FC<{ farmId: number; setCurrentStep: (step) => void; type?: NFT_TYPE }> = ({
  setCurrentStep,
  farmId,
  type,
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const { approve, isApproved, isLoading: gettingApproval, operatorAddress } = useNFTStakingApproval(farmId)
  const { data: nftBalanceOfUser } = useNftBalanceOfUser(farmId)

  useEffect(() => {
    if (isApproved) {
      if (nftBalanceOfUser > 0) {
        setCurrentStep(NFT_STAKE.emptyStaked)
      } else {
        setCurrentStep(NFT_STAKE.emptyNfts)
      }
    }
  }, [isApproved, nftBalanceOfUser, setCurrentStep])

  return (
    <Flex flexDirection="column" gap="30px" padding="0 24px 24px 24px">
      <Flex justifyContent="center">
        {type === NFT_TYPE.MINER_MOLE && (
          <NFTLogo alt="vvs_minerole_logo" src="/images/vvsMinerMole/vvs_miner_mole_logo.png" />
        )}
        {type === NFT_TYPE.ARGO && <NFTLogo alt="cornos_argo_logo" src="/images/cronos-icon.png" />}
      </Flex>
      <Flex flexDirection="column" gap="6px" alignItems="center">
        <Text fontFamily="Effra" fontSize="28px" lineHeight="33px">
          {t('Approve Contract')}
        </Text>
        <Text fontSize="16px" lineHeight="24px" color={theme.colors.textSubtle}>
          {type === NFT_TYPE.MINER_MOLE && t('Allow Miner Moles to be staked to all farms')}
          {type === NFT_TYPE.ARGO && t('Allow Cronos NFT to be staked')}
        </Text>
      </Flex>
      <Button
        variant="primary"
        scale="lg"
        onClick={() => {
          approve(operatorAddress)
        }}
        isLoading={gettingApproval}
        endIcon={gettingApproval && <AutoRenewIcon spin color="white" />}
      >
        {t('Approve Contract')}
      </Button>
    </Flex>
  )
}

const EmptyStaked: React.FC<{
  setCurrentStep: (step) => void
  setSelectedNftIds: (selectedNftIds) => void
  selectedNftIds: string[]
  farmId: number
  type: NFT_TYPE
  ecoNftCollections?: NftDetails[]
}> = ({ setCurrentStep, setSelectedNftIds, selectedNftIds, farmId, type, ecoNftCollections = [] }) => {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { colors } = useStyledTheme()
  const molesIds = useUserMoles((s) => s.value.moleIdsWithStaked)
  const { isMobile } = useMatchBreakpoints()

  const currentFarm = useDeserializedFarmFromPid(farmId, getFarmVersionFromPid(farmId))
  const ecoConfig = ecoFarm[currentFarm?.ecoFarm]

  const renderInfo = useMemo(() => {
    let info = {
      maxDesc: '',
      maxNum: 0,
      questionHelperText: '',
    }

    switch (type) {
      case NFT_TYPE.MINER_MOLE:
        info = {
          ...info,
          maxDesc: t('Select total maximum %maxNum% Miner Moles to stake', { maxNum: MAX_NFT_SELECTABLE }),
          maxNum: MAX_NFT_SELECTABLE,
          questionHelperText: t(
            'NFT boost factor will be determined by the rarity level and number of VVS Miner Moles staked.',
          ),
        }
        break

      case NFT_TYPE.ARGO:
        info = {
          ...info,
          maxDesc: t('Select total maximum %maxNum% NFTs to stake', { maxNum: ecoConfig?.maxStakedNum ?? 0 }),
          maxNum: ecoConfig?.maxStakedNum ?? 0,
          questionHelperText: t('NFT boost factor will be determined by the  number of NFTs staked.'),
        }
        break

      default:
        break
    }

    return info
  }, [ecoConfig, t, type])

  return (
    <Flex gap="16px" flexDirection="column" padding="24px 0" borderTop={`1px solid ${theme.colors.extraBackground}`}>
      <Flex flex={1} margin="0 24px">
        <StyledMessage filled variant="info" icon={<InfoIcon width={19.2} height={19.2} color={colors.primary} />}>
          <StyledMessageText>{renderInfo.maxDesc}</StyledMessageText>
        </StyledMessage>
      </Flex>
      <Flex>
        <NftSelectorView
          nftIds={molesIds}
          selectedNftIds={selectedNftIds}
          onChange={(selectedNfts) => {
            setSelectedNftIds(selectedNfts)
          }}
          type={type}
          dataList={ecoNftCollections}
          maxSelect={renderInfo.maxNum}
        />
      </Flex>
      <NftBoostFactorCard
        questionHelperText={renderInfo.questionHelperText}
        farmId={farmId}
        selectedNftIds={selectedNftIds}
        nftType={type}
        nftBoostFactor={1 + selectedNftIds?.length * ecoConfig?.boostFactorPerNFT}
      />
      <ModalActions>
        <Flex flex={1} margin={isMobile ? '0px 24px 24px 24px' : '0px 24px'}>
          <Button
            variant="primary"
            scale="lg"
            style={{ width: '100%' }}
            onClick={() => {
              setCurrentStep(NFT_STAKE.confirmStaked)
            }}
            disabled={selectedNftIds.length === 0}
          >
            {t('Continue')}
          </Button>
        </Flex>
      </ModalActions>
    </Flex>
  )
}

interface NftStakeConfirmStakedProps extends ConfirmStakedProps {
  selectedNftIds: string[]
  farmId: number
  isLoading?: boolean
  finalBoostingMoleIds?: any[]
  type?: NFT_TYPE
  ecoNftCollections?: NftDetails[]
  calculateType?: CALCULATE_TYPE
}

export const ConfirmStaked: React.FC<NftStakeConfirmStakedProps> = ({
  confirmBtnText,
  selectedNftIds,
  farmId,
  onClickConfirm,
  isLoading = false,
  finalBoostingMoleIds,
  type = NFT_TYPE.MINER_MOLE,
  ecoNftCollections = [],
  calculateType = CALCULATE_TYPE.STAKE_MORE,
}) => {
  const { theme } = useTheme()
  const { t } = useTranslation()

  const boostedAPRDetails = useGetBoostedAPRDetails(farmId, finalBoostingMoleIds || selectedNftIds)
  const ecoFarmBoostedAPRDetails = useGetEcoFarmBoostedAPRDetails(farmId, finalBoostingMoleIds || selectedNftIds)

  const renderInfo = useMemo(() => {
    let actionDesc = ''

    switch (calculateType) {
      case CALCULATE_TYPE.UN_STAKE:
        actionDesc = t('Unstake')
        break
      case CALCULATE_TYPE.STAKE_MORE:
      default:
        actionDesc = t('Deposit')
        break
    }

    let info = {
      title: '',
      aprTitle: '',
      stakedDesc: '',
      aprDetails: {},
    }

    switch (type) {
      case NFT_TYPE.MINER_MOLE:
        info = {
          ...info,
          title: `${actionDesc} ${t('Miner Moles:')}`,
          aprTitle: t('BOOSTED APR (ON TOP OF CURRENT xVVS VAULT BOOST)'),
          stakedDesc: t('Miner Moles Staked'),
          aprDetails: boostedAPRDetails,
        }
        break
      case NFT_TYPE.ARGO:
        info = {
          ...info,
          title: `${actionDesc} ${t('NFTs:')}`,
          aprTitle: t('Boosted APR'),
          stakedDesc: t('NFTs Staked'),
          aprDetails: ecoFarmBoostedAPRDetails,
        }
        break

      default:
        break
    }

    return info
  }, [boostedAPRDetails, calculateType, ecoFarmBoostedAPRDetails, t, type])

  return (
    <Flex gap="24px" flexDirection="column" paddingBottom="24px">
      <Flex padding="0 24px">
        <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
          {renderInfo.title}
        </Text>
      </Flex>
      <Flex>
        <NftSelectorView nftIds={selectedNftIds} dataList={ecoNftCollections} selectable={false} type={type} />
      </Flex>
      <Flex padding="0 24px">
        <Divider />
      </Flex>
      <NftBoostedAPRSection
        boostedAPRDetails={renderInfo.aprDetails as BoostedAPRDetails}
        aprTitle={renderInfo.aprTitle}
        stakedDesc={renderInfo.stakedDesc}
      />
      <Flex flex={1} padding="0 24px">
        <Button
          variant="primary"
          scale="lg"
          style={{ width: '100%' }}
          isLoading={isLoading}
          endIcon={isLoading && <AutoRenewIcon spin color="white" />}
          onClick={() => {
            onClickConfirm()
          }}
        >
          {confirmBtnText || t('Confirm NFT Stake')}
        </Button>
      </Flex>
    </Flex>
  )
}

export enum NFT_STAKE {
  approve = 'approve',
  emptyNfts = 'emptyNfts',
  emptyStaked = 'emptyStaked',
  loading = 'loading',
  confirmStaked = 'confirmStaked',
}

type NftStakeModalProps = {
  onDismiss?: () => void
  initStep?: NFT_STAKE
  farmId: number
  type?: NFT_TYPE
}
const NftStakeModal: React.FC<NftStakeModalProps> = ({ onDismiss, initStep, farmId, type = NFT_TYPE.MINER_MOLE }) => {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const [step, setStep] = useState<NFT_STAKE>(NFT_STAKE.loading)
  const [selectedNftIds, setSelectedNftIds] = useState<string[]>([])
  const currentFarm = useDeserializedFarmFromPid(farmId, getFarmVersionFromPid(farmId))
  const addTransaction = useTransactionAdder()

  const { mutateAsync: boostFarm, isLoading: isBoosting } = useStakeNftsAndBoostFarm()
  const { toastError } = useToast()

  const { data: userAllNftCollections } = useFormatLayerForNftDetails(farmId)
  const { mutateAsync: boostEcoFarm, isLoading: isEcoFarmBoosting } = useStakeNfts(getRewarderV2AddressByPid(farmId))

  const renderConfirmAction = useMemo(() => {
    let confirmAction
    let params = {}
    let isLoading = true

    switch (type) {
      case NFT_TYPE.ARGO:
        confirmAction = boostEcoFarm
        params = {
          farmId,
          nfts: selectedNftIds.map((tokenId) => ({
            tokenContract: getArgoNftAddress(),
            tokenId,
          })),
        }
        isLoading = isEcoFarmBoosting
        break
      case NFT_TYPE.MINER_MOLE:
      default:
        confirmAction = boostFarm
        params = {
          farmId,
          nfts: selectedNftIds.map((tokenId) => ({
            tokenContract: getVvsMinerMoleAddress(),
            tokenId,
          })),
        }
        isLoading = isBoosting
        break
    }

    return {
      confirmAction,
      params,
      isLoading,
    }
  }, [boostEcoFarm, boostFarm, farmId, isBoosting, isEcoFarmBoosting, selectedNftIds, type])

  const handleConfirm = useCallback(async () => {
    try {
      const { tx } = await renderConfirmAction.confirmAction(renderConfirmAction.params)
      addTransaction(tx, {
        title: t('Boost Farm & Stake NFT Completed'),
        summary: t('Boost %pair% farm with NFT %nftIds%', {
          pair: currentFarm?.lpSymbol,
          nftIds: selectedNftIds.map((id) => `#${id}`).join(', '),
        }),
      })
      onDismiss()
    } catch (e) {
      toastError(t('Transaction Failed'), e?.data?.message || e?.message) // smart contract msg || metamask msg
    }
  }, [renderConfirmAction, addTransaction, t, currentFarm, selectedNftIds, onDismiss, toastError])

  const modalTitle = useMemo<React.ReactNode>(() => {
    let title: React.ReactNode | string = ''
    switch (step) {
      case NFT_STAKE.emptyStaked:
        title = (
          <Flex gap="12px" flexDirection="column" alignItems="center" justifyContent="center">
            <Text
              fontFamily="Effra"
              fontSize="28px"
              lineHeight="33px"
              color={theme.colors.text}
              textAlign="center"
              width={['80%', '80%', 'auto']}
            >
              {t('NFT to Stake from Your Wallet')}
            </Text>
            <Text textAlign="center" fontSize="16px" lineHeight="24px" color={theme.colors.textSubtle}>
              {type === NFT_TYPE.MINER_MOLE && t('Stake rarer NFTs for higher NFT boost multipliers (up to 3x)')}
              {type === NFT_TYPE.ARGO && t('Stake Cronos NFTs for higher NFT boost multipliers')}
            </Text>
          </Flex>
        )
        break
      case NFT_STAKE.confirmStaked:
        title = (
          <Flex gap="12px" flexDirection="column" alignItems="center" justifyContent="center">
            <Text
              fontFamily="Effra"
              fontSize="28px"
              lineHeight="33px"
              color={theme.colors.text}
              textAlign="center"
              width={['80%', '80%', 'auto']}
            >
              {t('Confirm NFT Staking')}
            </Text>
          </Flex>
        )
        break
      case NFT_STAKE.approve:
      case NFT_STAKE.emptyNfts:
      case NFT_STAKE.loading:
      default:
        break
    }
    return title
  }, [step, t, theme, type])

  const renderContent = useMemo<React.ReactNode>(() => {
    let content = <Skeleton height="300px" />

    switch (step) {
      case NFT_STAKE.emptyStaked:
        content = (
          <EmptyStaked
            setCurrentStep={setStep}
            setSelectedNftIds={setSelectedNftIds}
            selectedNftIds={selectedNftIds}
            farmId={farmId}
            type={type}
            ecoNftCollections={userAllNftCollections}
          />
        )
        break
      case NFT_STAKE.confirmStaked:
        content = (
          <ConfirmStaked
            onClickConfirm={handleConfirm}
            selectedNftIds={selectedNftIds}
            farmId={farmId}
            isLoading={renderConfirmAction.isLoading}
            type={type}
            ecoNftCollections={userAllNftCollections.filter((nft) => selectedNftIds.find((item) => item === nft.id))}
          />
        )
        break
      case NFT_STAKE.approve:
        content = <ApproveContract farmId={farmId} type={type} setCurrentStep={setStep} />
        break
      case NFT_STAKE.emptyNfts:
        content = <EmptyNFTs type={type} />
        break
      case NFT_STAKE.loading:
        content = (
          <Flex justifyContent="center" alignItems="center" margin="50px">
            <Skeleton width="460px" height="300px" />
          </Flex>
        )
        break
      default:
        break
    }
    return content
  }, [step, selectedNftIds, farmId, type, userAllNftCollections, handleConfirm, renderConfirmAction.isLoading])

  useEffect(() => {
    if (initStep) {
      setStep(initStep)
    }
  }, [initStep])

  return (
    <StyledModal
      bodyPadding="0px"
      title={modalTitle}
      onDismiss={onDismiss}
      modalStep={step}
      closeIconColor={theme.colors.text}
      onBack={
        step === NFT_STAKE.confirmStaked
          ? () => {
              setStep(NFT_STAKE.emptyStaked)
            }
          : undefined
      }
    >
      {renderContent}
    </StyledModal>
  )
}

export default NftStakeModal
